<script>
    import { apiMethods } from "../../../Services/index.svelte";

    import { user } from "../../../stores";

    import {
        SvelteTable,
        LoadingModal,
        CardLayout,
    } from "../../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getFormatDate,
        getFormatDateAndTime,
        getInvoiceStatus,
        getText,
        goToDownloadPage,
        goToURL,
        handleDownloadPDF,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";
    import { DirectUserRoles } from "../../../Utils/utilEnums.svelte";

    export let invoicesDetails = [];

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    let isDownloadingAccountInvoice = false,
        isRegeneratingAccountInvoice = false,
        isSendingInvoiceToDoctor = false,
        isInvoiceSendToDoctor = false;

    const columns = [
        {
            key: "invoiceId",
            title: "Invoice ID",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "invoiceNum",
            title: "Invoice Number",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.invoiceNumber),
        },
        {
            key: "createDate",
            title: "Fortnight ending",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.createdDate, false, true),
        },
        {
            key: "expireDate",
            title: "Due Date",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.dueDate, v.isExpired, true),
        },
        {
            key: "subTotal",
            title: "Sub-Total",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee / 10),
        },
        {
            key: "totalFee",
            title: "Total",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) =>
                getAmountInCurrency(v.totalFee * (v.addGST ? 1.1 : 1)),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getStatus(v.status),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "actions") {
            if (className.includes("downloadIcon"))
                downloadAccountInvoice(rowData.id);
            else if (className.includes("reGenerateIcon"))
                regenerateAccountInvoice(rowData);
            else if (className.includes("sendToDoctorIcon"))
                sendInvoice(rowData.id);
        } else if (rowData.id) goToURL(`/AccountInvoice/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const downloadAccountInvoice = (id, isRegenerate) => {
        goToDownloadPage(5, id, isRegenerate);
    };

    const regenerateAccountInvoice = async (data) => {
        const regenerateObj = {
            type: 5,
            entityId: data.id,
            doctorId: data.doctorId,
            patientId: 0,
            patientEncounterId: 0,
        };

        isRegeneratingAccountInvoice = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadAccountInvoice(data.id, true);
                else
                    showError(
                        "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingAccountInvoice = false;
    };

    const sendInvoice = async (id) => {
        isSendingInvoiceToDoctor = true;

        await apiMethods.getMethod
            .getAccountInvoiceById(id)
            .then(async (res) => {
                if (!res || !res.id || res.error)
                    showError(
                        "Failed to load invoice details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    await apiMethods.postMethod
                        .sendInvoiceToDoctor(res)
                        .then((res) => {
                            if (res.id) isInvoiceSendToDoctor = true;
                            else
                                showError(
                                    "Failed to send invoice to doctor, please refresh the page and try again and contact support if the issue persists.",
                                );
                        });
            })
            .catch((err) => showException(err));

        isSendingInvoiceToDoctor = false;
    };

    // HELPER FUNCTIONS
    const _getFormatDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    const _getStatus = (status) => {
        let statusText, statusColor;

        statusText = getInvoiceStatus(status);

        switch (status) {
            case 1:
            case 2:
                statusColor = "#2DBD6F";
                break;
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    ${
                        isUserAdmin
                            ? `<div class="reGenerateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Re-generate Invoice PDF">
                                <i class="reGenerateIcon fas fa-arrows-rotate"></i>
                            </div>`
                            : ""
                    }

                    <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Invoice PDF">
                        <i class="downloadIcon fas fa-download"></i>
                    </div>

                ${
                    isUserDoctor
                        ? ""
                        : `<div class="sendToDoctorIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Send Invoice to Doctor">
                        <i class="sendToDoctorIcon fa-solid fa-paper-plane"></i>
                    </div>`
                }
                    
                </div>`;
    };
</script>

<CardLayout title="Doctor Invoices">
    {#if invoicesDetails.length}
        <div class="flex flex-1 overflow-auto">
            <SvelteTable
                {columns}
                rows={invoicesDetails}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
                clickColFunc={({ detail: { event, row, key } }) => {
                    handleClickCol(event, row, key);
                }}
            />
        </div>
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>
                    No invoices {isUserDoctor
                        ? ""
                        : "currently under this doctor"}
                </p>
            </div>
        </div>
    {/if}
</CardLayout>

{#if isDownloadingAccountInvoice}
    <LoadingModal titleText="Downloading Invoice PDF..." />
{/if}

{#if isRegeneratingAccountInvoice}
    <LoadingModal titleText="Regenerating Invoice PDF..." />
{/if}

{#if isSendingInvoiceToDoctor}
    <LoadingModal titleText="Sending Invoice to Doctor..." />
{/if}

<div
    class={isInvoiceSendToDoctor ? "modal modal-open sm:modal-middle" : "modal"}
>
    <div class="modal-box">
        <div class="p-4">
            <p class="leading-6 text-secondary whitespace-pre-line">
                Invoice send to doctor.
            </p>
        </div>
        <div class="modal-action">
            <button
                class="btn btn-secondary text-white btn-sm border-none w-24"
                on:click={() => (isInvoiceSendToDoctor = false)}
            >
                Done
            </button>
        </div>
    </div>
</div>
